import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import CommonCard from '../../components/card/CommonCard';
import WeeklyChart from '../../components/charts/barChart/weeklyChart';
import ProgressBar from '../../components/charts/progressBar';
import ChartSection from '../../components/charts/chartSection';
import { formatDateTime } from '../../utils/base';
import { physicalRecordAPI } from '../../actions/imsServer';
import { CHALLENGE_HEARTBEAT_MAX, API_PAGE_SIZE } from '../../constants';

export const ReportHeartbeat = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    // 사용자 정보
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // 데이터 상태 관리
    const [physicalRecords, setPhysicalRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());

    // ContentArea에 이벤트 리스너 추가 (디버깅용) - 제거
    // ContentArea의 React onMouseDown 핸들러로 대체

    // 데이터 로딩 함수
    const fetchPhysicalRecords = async (startDate, endDate, isInitialLoad = false) => {
        if (!meInfo.id) return;
        try {
            if (isInitialLoad) {
                setLoading(true);
            }
            setError(null);

            const startStr = formatDateTime(startDate, '-', ':', 0, 10);
            const endStr = formatDateTime(endDate, '-', ':', 0, 10);
            let allRecords = [];
            let offset = 0;
            let page;
            do {
                const response = await physicalRecordAPI.getPhysicalRecords({
                    userId: meInfo.id,
                    state: 0,
                    startDate: startStr,
                    endDate: endStr,
                    offset
                });
                page = response?.data?.physicalRecords || [];
                allRecords = allRecords.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            if (allRecords.length > 0) {
                // 기존 데이터와 병합 (중복 제거)
                setPhysicalRecords(prevRecords => {
                    const existingDates = new Set(prevRecords.map(r => formatDateTime(new Date(r.date), '-', ':', 0, 10)));
                    const uniqueNewRecords = allRecords.filter(r =>
                        !existingDates.has(formatDateTime(new Date(r.date), '-', ':', 0, 10))
                    );
                    return [...prevRecords, ...uniqueNewRecords].sort((a, b) =>
                        new Date(a.date) - new Date(b.date)
                    );
                });
            }
        } catch (err) {
            console.error('심박수 데이터 로딩 실패:', err);
            setError(err.message);
        } finally {
            if (isInitialLoad) {
                setLoading(false);
            }
        }
    };

    // 초기 데이터 로드 (컴포넌트 마운트 시)
    useEffect(() => {
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 89); // 과거 3개월 (90일) - 더 많은 데이터 로드

        fetchPhysicalRecords(startDate, today, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPhysicalRecords는 의존성에서 제외
    }, [meInfo.id]);

    // 스크롤로 새로운 구간 로드
    const loadMoreData = (direction) => {
        if (physicalRecords.length === 0) return;

        const sortedRecords = [...physicalRecords].sort((a, b) => new Date(a.date) - new Date(b.date));
        const earliestDate = new Date(sortedRecords[0].date);
        const latestDate = new Date(sortedRecords[sortedRecords.length - 1].date);

        if (direction === 'past') {
            // 과거 데이터 더 로드
            const newStartDate = new Date(earliestDate);
            newStartDate.setDate(newStartDate.getDate() - 14);
            const newEndDate = new Date(earliestDate);
            newEndDate.setDate(newEndDate.getDate() - 1);

            fetchPhysicalRecords(newStartDate, newEndDate, false);
        } else if (direction === 'future') {
            // 미래 데이터 더 로드 (오늘까지만)
            const today = new Date();
            const newStartDate = new Date(latestDate);
            newStartDate.setDate(newStartDate.getDate() + 1);
            const newEndDate = new Date(Math.min(today.getTime(), latestDate.getTime() + 14 * 24 * 60 * 60 * 1000));

            if (newStartDate <= today) {
                fetchPhysicalRecords(newStartDate, newEndDate, false);
            }
        }
    };

    // 주간 심박수 데이터 생성 (1년치 고정)
    const allWeeklyData = useMemo(() => {
        const data = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const earliestDate = new Date(today);
        earliestDate.setDate(today.getDate() - 364);
        earliestDate.setHours(0, 0, 0, 0);
        const totalDays = 365;

        for (let i = 0; i < totalDays; i++) {
            const date = new Date(earliestDate);
            date.setDate(earliestDate.getDate() + i);

            const dateStr = formatDateTime(date, '/', ':', 0, 10);
            const record = physicalRecords.find(r => {
                const recordDate = new Date(r.date);
                recordDate.setHours(0, 0, 0, 0);
                return formatDateTime(recordDate, '/', ':', 0, 10) === dateStr;
            });

            const minHeartRate = record?.minHeartRate || null;
            const maxHeartRate = record?.maxHeartRate || null;
            const isCurrent = date.toDateString() === today.toDateString(); // 오늘이 현재 날짜

            data.push({
                day: dateStr,
                min: minHeartRate,
                max: maxHeartRate,
                cur: isCurrent ? (record?.curHeartRate || null) : null, // 최근 기록 (오늘만)
                isCurrent,
                color: theme.palette.figma.heartRate
            });
        }

        return data;
    }, [physicalRecords, theme.palette.figma.heartRate]);

    // 시간별 심박수 데이터 생성 (24시간)
    const hourlyHeartbeatData = useMemo(() => {
        // 선택된 날짜의 데이터 찾기
        const selectedDateStr = formatDateTime(selectedDate, '/', ':', 0, 10);
        const selectedRecord = physicalRecords.find(record => {
            const recordDate = formatDateTime(new Date(record.date), '/', ':', 0, 10);
            return recordDate === selectedDateStr;
        });

        // 기본값으로 24시간 데이터 생성 [min, max, avg]
        const defaultValue = [0, 0, 0];
        const defaultData = Array(24).fill(defaultValue);

        if (selectedRecord) {
            try {
                // hourlyHeartRates가 JSON 문자열인 경우 파싱
                let hourlyHeartRates = selectedRecord.hourlyHeartRates;
                if (typeof hourlyHeartRates === 'string') {
                    hourlyHeartRates = JSON.parse(hourlyHeartRates);
                }

                if (Array.isArray(hourlyHeartRates) && hourlyHeartRates.length >= 24) {
                    return hourlyHeartRates.slice(0, 24).map(hourData => {
                        if (Array.isArray(hourData) && hourData.length >= 3) {
                            const [min, max, avg] = hourData;
                            // 데이터가 모두 0이 아닌 경우에만 사용
                            if (min > 0 && max > 0 && avg > 0) {
                                return [min, max, avg];
                            }
                        }
                        return defaultValue; // 기본값
                    });
                }
            } catch (e) {
                console.error('시간별 심박수 데이터 파싱 실패:', e);
            }
        }

        return defaultData;
    }, [physicalRecords, selectedDate]);

    // 선택된 시간대 상태
    const [selectedHourIndex, setSelectedHourIndex] = useState(16); // 기본값: 16시 (오후 4시)

    // 선택된 시간대 데이터 계산
    const selectedHourData = hourlyHeartbeatData[selectedHourIndex];
    const selectedHour = selectedHourIndex;
    const selectedHourRange = `${selectedHour.toString().padStart(2, '0')}:00 - ${selectedHour.toString().padStart(2, '0')}:59`;
    const selectedHeartbeatRange = `${selectedHourData?.[0]} - ${selectedHourData?.[1]} bmp`;

    // 현재 심박수 데이터 (선택된 날짜 기준)
    const selectedDayData = useMemo(() => {
        const selectedDateStr = formatDateTime(selectedDate, '/', ':', 0, 10);
        return allWeeklyData.find(item => item.day === selectedDateStr) || allWeeklyData[allWeeklyData.length - 1];
    }, [allWeeklyData, selectedDate]);

    const popupHeight = 27;

    return (
        <ContentArea sx={{ paddingBottom: '100px' }}>
            {/* 로딩 상태 */}
            {loading && (
                <CommonCard>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        {t('loading_data')}
                    </div>
                </CommonCard>
            )}

            {/* 에러 상태 */}
            {error && (
                <CommonCard>
                    <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                        {t('an_error_occurred_while_loading_data_message', { message: error })}
                    </div>
                </CommonCard>
            )}

            {/* 메인 콘텐츠 */}
            {!loading && !error && (
                <Box sx={{
                    width: '100%',
                    maxWidth: '100%',
                    margin: '0 auto',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    // 큰 화면에서도 적절한 너비 유지
                    '@media (min-width: 600px)': {
                        maxWidth: '600px',
                    },
                    '@media (min-width: 900px)': {
                        maxWidth: '800px',
                    },
                }}>
                    {/* 주간 심박수 차트 */}
                    <WeeklyChart
                        data={allWeeklyData} // 14개 데이터 전달
                        color={theme.palette.figma.heartRate} // 심박수 색상
                        height={50}
                        barWidth={10}
                        gap={28}
                        visibleItems={7} // 7개 바만 표시
                        startFromEnd={true} // 끝에서부터 표시 (오늘이 오른쪽 끝)
                        targetValue={CHALLENGE_HEARTBEAT_MAX} // 전체 최대 심박수
                        yAxisLabels={['', '140', '', '40']} // Y축 라벨 (140, 40)
                        isRangeData={true}
                        selectedDate={selectedDate}
                        onDateSelect={(item) => {
                            // 날짜 클릭 시 선택된 날짜 업데이트
                            const [year, month, day] = item.day.split('/');
                            const clickedDate = new Date(year, month - 1, day);
                            setSelectedDate(clickedDate);

                            // 다른 날짜를 클릭했을 때 시간별 차트의 팝업과 가이드라인 제거
                            setSelectedHourIndex(-1);
                        }}
                        onScrollToEnd={(direction) => {
                            loadMoreData(direction);
                        }}
                    />

                    {/* 심박수 요약 */}
                    {(selectedDayData.min > 0 && selectedDayData.max > 0) ? <CommonCard>
                        {/* 메인 심박수 범위 표시 */}
                        <Box sx={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                        }}>
                            <><Typography sx={{
                                fontSize: { xs: '32px', sm: '36px', md: '40px' },
                                fontWeight: 600,
                                letterSpacing: '-1.2px',
                                lineHeight: 1,
                                whiteSpace: 'nowrap'
                            }}>
                                {selectedDayData.min} - {selectedDayData.max}
                            </Typography>
                                <Typography sx={{
                                    position: 'absolute',
                                    right: '-5px',
                                    top: '50%',
                                    transform: 'translate(100%, -50%)',
                                    color: theme.palette.figma.white[60],
                                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                    fontWeight: 400,
                                    letterSpacing: '-0.42px',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap'
                                }}>
                                    bpm
                                </Typography></>
                        </Box>

                        {/* 진행률 바 섹션 */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            alignItems: 'center',
                            width: '100%'
                        }}>
                            {/* ProgressBar 컴포넌트 사용 */}
                            <ProgressBar
                                rangeStart={40}
                                rangeEnd={CHALLENGE_HEARTBEAT_MAX}
                                start={selectedDayData.min}
                                end={selectedDayData.max}
                                height="30px"
                                showCurrentIndicator={false}
                                showProgress={true}
                                showStartEndValues={true}
                                backgroundColor={theme.palette.figma.white[20]}
                                progressColor={theme.palette.figma.heartRate}
                                borderRadius="20px"
                                sx={{
                                    width: '100%',
                                    maxWidth: '300px',
                                    minWidth: '200px',
                                }}
                            />
                        </Box>

                        {/* 최근 기록 - 선택된 날짜가 오늘인 경우에만 표시 */}
                        {selectedDayData.isCurrent && selectedDayData.cur && (
                            <Box sx={{
                                display: 'flex',
                                gap: '5px',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Typography sx={{
                                    color: theme.palette.figma.heartRate,
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    letterSpacing: '-0.42px',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap'
                                }}>
                                    {selectedDayData.cur}
                                </Typography>
                                <Typography sx={{
                                    color: theme.palette.figma.white[60],
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    letterSpacing: '-0.36px',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap'
                                }}>
                                    {t('recent_record')} {formatDateTime(selectedDayData.day, '/', ':', 0, 10)}
                                </Typography>
                            </Box>
                        )}
                    </CommonCard> : <></>}

                    {/* 시간별 심박수 차트 */}
                    {selectedDayData.min > 0 && selectedDayData.max > 0 && <CommonCard sx={{ alignItems: 'flex-start' }}>
                        <ChartSection
                            title={t('hourly_min_max_heart_rate')}
                            renderChart={
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px',
                                    width: '100%'
                                }}>
                                    {/* 차트 영역 */}
                                    <Box sx={{
                                        display: 'flex',
                                        gap: '5px',
                                        alignItems: 'flex-start',
                                        width: '100%',
                                        position: 'relative'
                                    }}>
                                        {/* 메인 차트 영역 */}
                                        <Box sx={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '3px',
                                            overflow: 'hidden'
                                        }}>
                                            {/* 선택된 시간대 팝업 - selectedHourIndex가 유효할 때만 표시 */}
                                            {selectedHourIndex >= 0 && (
                                                <Box sx={{
                                                    height: `${popupHeight}px`,
                                                    position: 'absolute',
                                                    width: '100%'
                                                }}>
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: (() => {
                                                            let _index = selectedHourIndex;

                                                            if (selectedHourIndex <= 2) {
                                                                _index = 3;
                                                            } else if (selectedHourIndex >= 19) {
                                                                _index = 18
                                                            }
                                                            return `${_index * 4 + 2.5}%`;
                                                        })(),
                                                        top: 0,
                                                        display: 'flex',
                                                        gap: '5px',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: '5px 10px',
                                                        backgroundColor: theme.palette.figma.heartRateDark,
                                                        borderRadius: '10px',
                                                        zIndex: 1,
                                                        transform: (() => {
                                                            return 'translateX(-50%)';
                                                        })()
                                                    }}>
                                                        <Typography sx={{
                                                            color: theme.palette.figma.white[80],
                                                            fontSize: '12px',
                                                            fontWeight: 400,
                                                            letterSpacing: '-0.36px',
                                                            lineHeight: 1,
                                                            textAlign: 'right',
                                                            whiteSpace: 'nowrap',
                                                            zIndex: 3
                                                        }}>
                                                            {selectedHourRange}
                                                        </Typography>
                                                        <Typography sx={{
                                                            color: theme.palette.figma.heartRate,
                                                            fontSize: '14px',
                                                            fontWeight: 500,
                                                            letterSpacing: '-0.42px',
                                                            lineHeight: 1,
                                                            textAlign: 'right',
                                                            whiteSpace: 'nowrap',
                                                            zIndex: 2
                                                        }}>
                                                            {selectedHeartbeatRange}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* 24시간 바 차트 */}
                                            <Box sx={{
                                                height: '100px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                position: 'relative',
                                                width: '100%'
                                            }}>
                                                {/* Y축 그리드 라인 */}
                                                {/* 140 그리드 라인 */}
                                                <Box sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    right: '20px',
                                                    top: '44px', // 140 위치 ((CHALLENGE_HEARTBEAT_MAX-140)/180)*100 = 44px
                                                    height: '1px',
                                                    backgroundColor: theme.palette.figma.white[30],
                                                    zIndex: 0
                                                }} />

                                                {/* 140 라벨 */}
                                                <Typography sx={{
                                                    position: 'absolute',
                                                    right: '0px',
                                                    top: '40px', // 44px - 4px (라벨 중앙 정렬)
                                                    color: theme.palette.figma.white[60],
                                                    fontSize: '10px',
                                                    fontWeight: 500,
                                                    letterSpacing: '-0.3px',
                                                    lineHeight: 1,
                                                    whiteSpace: 'nowrap',
                                                    zIndex: 1
                                                }}>
                                                    140
                                                </Typography>

                                                {/* 40 그리드 라인 */}
                                                <Box sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    right: '20px',
                                                    bottom: '0px', // 40 위치 (맨 아래)
                                                    height: '1px',
                                                    backgroundColor: theme.palette.figma.white[30],
                                                    zIndex: 0
                                                }} />

                                                {/* 40 라벨 */}
                                                <Typography sx={{
                                                    position: 'absolute',
                                                    right: '0px',
                                                    bottom: '-4px', // 0px - 4px (라벨 중앙 정렬)
                                                    color: theme.palette.figma.white[60],
                                                    fontSize: '10px',
                                                    fontWeight: 400,
                                                    letterSpacing: '-0.3px',
                                                    lineHeight: 1,
                                                    whiteSpace: 'nowrap',
                                                    zIndex: 1
                                                }}>
                                                    40
                                                </Typography>

                                                {/* 24개 바 생성 */}
                                                {hourlyHeartbeatData.map((heartbeatData, index) => {
                                                    const [min, max] = heartbeatData;

                                                    // Y축 범위 40-CHALLENGE_HEARTBEAT_MAX 차트 전체 높이(100px)에 매핑
                                                    const yAxisMin = 40;
                                                    const yAxisMax = CHALLENGE_HEARTBEAT_MAX;
                                                    const yAxisRange = yAxisMax - yAxisMin; // 180

                                                    // 바의 상단 위치 계산 (max 값 기준)
                                                    // CHALLENGE_HEARTBEAT_MAX은 0px(top), 40은 100px(bottom)에 위치
                                                    const maxPosition = ((yAxisMax - max) / yAxisRange) * 100; // CHALLENGE_HEARTBEAT_MAX에서 max까지의 거리
                                                    const minPosition = ((yAxisMax - min) / yAxisRange) * 100; // CHALLENGE_HEARTBEAT_MAX에서 min까지의 거리

                                                    const barTop = maxPosition; // 0px에서 시작해서 아래로
                                                    const barHeight = minPosition - maxPosition;

                                                    // X축에 맞춰 바 위치 계산 (24개 바를 균등하게 배치)                                            
                                                    const barWidth = 3.4; // 바 너비
                                                    const gap = 0.5; // 바 사이 간격                                            
                                                    const barLeft = index * (barWidth + gap);

                                                    if (min === 0 && max === 0) {
                                                        return (
                                                            <Box key={index} sx={{
                                                                height: '100px',
                                                                width: `${barWidth}%`,
                                                            }} />
                                                        );
                                                    }

                                                    return (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                height: '100px',
                                                                width: `${barWidth}%`,
                                                                position: 'absolute',
                                                                left: `${barLeft}%`,
                                                                zIndex: 25,
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => setSelectedHourIndex(index)}
                                                        >
                                                            <Box sx={{
                                                                display: (selectedHourIndex >= 0 && selectedHourIndex === index) ? 'flex' : 'none',
                                                                position: 'absolute',
                                                                left: `50%`, // 실제 바차트 위치 기준
                                                                top: `${popupHeight}px`,
                                                                width: '1px', // 가로너비 1px
                                                                height: `${barTop - popupHeight}px`,
                                                                backgroundColor: theme.palette.figma.heartRateDark,
                                                            }} />
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: 0,
                                                                top: `${barTop}px`,
                                                                width: '100%',
                                                                height: `${barHeight}px`,
                                                                backgroundColor: theme.palette.figma.heartRate,
                                                                borderRadius: '4px',
                                                            }} />
                                                        </Box>
                                                    );
                                                })}


                                            </Box>

                                            {/* X축 시간 표시 */}
                                            <Box sx={{
                                                height: '4px',
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                justifyContent: 'space-between',
                                                width: 'calc(100% - 20px)'
                                            }}>
                                                {[0, 1, 2, 3].map((_, index) => (
                                                    <Box key={index} sx={{ flex: 1, display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                        <Box sx={{
                                                            height: '4px',
                                                            width: '1px',
                                                            backgroundColor: theme.palette.figma.white[30],
                                                            borderRadius: '2px'
                                                        }} />
                                                    </Box>
                                                ))}
                                            </Box>

                                            {/* X축 라벨 */}
                                            <Box sx={{
                                                height: '14px',
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                justifyContent: 'space-between',
                                                width: 'calc(100% - 20px)'
                                            }}>
                                                {['00:00', '06:00', '12:00', '18:00'].map((time, index) => (
                                                    <Box key={index} sx={{ flex: 1 }}>
                                                        <Typography sx={{
                                                            color: theme.palette.figma.white[60],
                                                            fontSize: '12px',
                                                            fontWeight: 400,
                                                            letterSpacing: '-0.36px',
                                                            lineHeight: 1,
                                                            textAlign: 'left'
                                                        }}>
                                                            {time}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>

                                    </Box>

                                </Box>
                            }
                        />
                    </CommonCard>}
                </Box>
            )}
        </ContentArea>
    );
};

export default ReportHeartbeat;